// Dev-panel local port of agents-cdn's lib/component-types.
//
// Kept local (instead of using @ringg/core's component types) because the
// source contract diverges from core in ways the preview renderer relies on:
//   - `SimpleComponentPayload` includes `BlocksPayload` here (core's does not)
//   - `ButtonsData` carries `presentation` / `completionDisplay` (not in core)
//   - `BlocksPayload.data` is the structured `BlocksConfig` (core keeps blocks opaque)
//   - `buildPayload` interpolates single-brace `{placeholder}` templates
//   - `formatSlotDate` uses the short weekday/month format
import { BlocksConfig } from "./block-types";

export interface ComponentTheme {
  primaryColor?: string;
  primaryTextColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  textColor?: string;
  mutedTextColor?: string;
  borderColor?: string;
  errorColor?: string;
  successColor?: string;
  fontFamily?: string;
  fontSize?: string;
  borderRadius?: string;
  padding?: string;
  buttonStyle?: "rounded" | "pill" | "square";
  buttonSize?: "sm" | "md" | "lg";
}

export interface BaseComponent {
  component_type: string;
  component_id: string;
  data: unknown;
  theme?: ComponentTheme;
}

export interface CalendarSlot {
  id: string;
  datetime: string;
}

export interface CalendarBookingData {
  available_slots: CalendarSlot[];
  timezone: string;
  title: string;
}

export interface CalendarBookingPayload extends BaseComponent {
  component_type: "calendar_booking";
  data: CalendarBookingData;
}

export type FormFieldType = "text" | "email" | "tel" | "number" | "select" | "multiselect" | "textarea" | "date" | "boolean";

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormField {
  name: string;
  type: FormFieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[] | FormFieldOption[]; // For select fields
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    message?: string;
  };
}

export interface FormData {
  title: string;
  description?: string;
  fields: FormField[];
  submit_label?: string;
}

export interface FormPayload extends BaseComponent {
  component_type: "form";
  data: FormData;
}

export interface ButtonAction {
  type: "navigate" | "trigger_component" | "api_call";
  url?: string;
  component_type?: string;
  method?: "POST" | "GET";
  endpoint?: string;
  payload?: Record<string, string>;
}

export interface ButtonItem {
  id: string;
  label: string;
  style?: "primary" | "secondary" | "outline" | "destructive";
  action: ButtonAction;
}

export interface ButtonsData {
  title?: string;
  presentation?: "boxed" | "free";
  completionDisplay?: "confirmation" | "selected_item";
  buttons: ButtonItem[];
}

export interface ButtonsPayload extends BaseComponent {
  component_type: "buttons";
  data: ButtonsData;
}

export interface ConfirmationData {
  title: string;
  message: string;
  icon?: "success" | "info" | "warning" | "error";
  details?: Array<{ label: string; value: string }>;
}

export interface ConfirmationPayload extends BaseComponent {
  component_type: "confirmation";
  data: ConfirmationData;
}

export interface FlowStep {
  id: string;
  title: string;
  component: SimpleComponentPayload;
}

export interface InteractiveFlowData {
  title?: string;
  steps: FlowStep[];
  // Final action after all steps complete
  on_complete: {
    method: "POST" | "GET";
    endpoint: string;
    payload: Record<string, string>;
  };
}

export interface InteractiveFlowPayload extends BaseComponent {
  component_type: "interactive_flow";
  data: InteractiveFlowData;
}

// Block Kit widget delivered over the `ringg.blocks` text stream (see lib/block-types.ts)
export interface BlocksPayload extends BaseComponent {
  component_type: "blocks";
  data: BlocksConfig;
}

export type ComponentPayload = CalendarBookingPayload | FormPayload | ButtonsPayload | ConfirmationPayload | InteractiveFlowPayload | BlocksPayload;

export type SimpleComponentPayload = CalendarBookingPayload | FormPayload | ButtonsPayload | ConfirmationPayload | BlocksPayload;

export interface ComponentActionResponse {
  success: boolean;
  message: string;
  next_step?: SimpleComponentPayload;
  confirmation?: ConfirmationData;
}

export interface GroupedSlots {
  date: string;
  dateLabel: string;
  slots: CalendarSlot[];
}

export function groupSlotsByDate(slots: CalendarSlot[]): GroupedSlots[] {
  const grouped = new Map<string, CalendarSlot[]>();

  slots.forEach((slot) => {
    const date = new Date(slot.datetime);
    const dateKey = date.toISOString().split("T")[0] ?? "";

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(slot);
  });

  return Array.from(grouped.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, dateSlots]) => {
      const date = new Date(dateKey);
      return {
        date: dateKey,
        dateLabel: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
        slots: dateSlots.sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()),
      };
    });
}

export function formatSlotTime(datetime: string, timezone?: string): string {
  const date = new Date(datetime);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  });
}

export function formatSlotDate(datetime: string, timezone?: string): string {
  const date = new Date(datetime);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
}

// Replace placeholders in payload with actual values
export function buildPayload(template: Record<string, string>, values: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(template)) {
    if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
      const placeholder = value.slice(1, -1);
      result[key] = values[placeholder] ?? value;
    } else {
      result[key] = value;
    }
  }

  return result;
}

// Type guard functions
export function isCalendarBooking(payload: ComponentPayload): payload is CalendarBookingPayload {
  return payload.component_type === "calendar_booking";
}

export function isForm(payload: ComponentPayload): payload is FormPayload {
  return payload.component_type === "form";
}

export function isButtons(payload: ComponentPayload): payload is ButtonsPayload {
  return payload.component_type === "buttons";
}

export function isConfirmation(payload: ComponentPayload): payload is ConfirmationPayload {
  return payload.component_type === "confirmation";
}

export function isInteractiveFlow(payload: ComponentPayload): payload is InteractiveFlowPayload {
  return payload.component_type === "interactive_flow";
}

export function isBlocks(payload: ComponentPayload): payload is BlocksPayload {
  return payload.component_type === "blocks";
}
